
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { getMainDeadline } from '@/utils/dateFormatters';
import { TenderData } from '@/types/tender';

export type TenderNotification = {
  id: string;
  tender_data: TenderData;
  created_at: string;
  tender_id: string;
  is_read: boolean;
  title?: string;
  organization?: string;
  deadline?: string;
};

export const useNotifications = () => {
  const [notifications, setNotifications] = useState<TenderNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Function to extract text from multilingual objects or string arrays
  const extractText = (obj: any): string | undefined => {
    if (!obj) return undefined;
    
    if (typeof obj === "string") return obj;
    
    if (Array.isArray(obj)) {
      return obj.length > 0 ? String(obj[0]) : undefined;
    }
    
    if (typeof obj === "object") {
      // Try to get Danish version first since this is primarily for Danish users
      if (obj.dan) {
        if (Array.isArray(obj.dan)) {
          return obj.dan.length > 0 ? String(obj.dan[0]) : undefined;
        }
        return String(obj.dan);
      }
      
      // Try to get English version if Danish not available
      if (obj.eng) {
        if (Array.isArray(obj.eng)) {
          return obj.eng.length > 0 ? String(obj.eng[0]) : undefined;
        }
        return String(obj.eng);
      }
      
      // If no Danish or English, try any other language
      for (const key of Object.keys(obj)) {
        const value = obj[key];
        if (Array.isArray(value)) {
          if (value.length > 0) return String(value[0]);
        } else {
          return String(value);
        }
      }
    }
    
    return undefined;
  };

  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      // Get recent tenders that match user preferences
      const { data: recentTenders, error: recentTendersError } = await supabase
        .from('saved_tenders')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      
      if (recentTendersError) throw recentTendersError;
      
      // Get the IDs of tenders the user has already seen
      const { data: seenTenderData } = await supabase
        .from('user_seen_tenders')
        .select('tender_id, sent_at')
        .eq('user_id', user.id);
      
      // Create a set of seen tender IDs for fast lookup
      const seenTenderIds = new Set(seenTenderData?.map(item => item.tender_id) || []);
      
      // Find tenders that haven't been seen yet
      const unseenTenders = (recentTenders || []).filter(tender => {
        const tenderData = tender.tender_data as TenderData;
        const tenderId = tenderData && typeof tenderData === 'object' ? 
                        (tenderData.publication_number || 
                        tenderData.id || 
                        tender.tender_id || 
                        tender.id) : tender.id;
        return tenderId && !seenTenderIds.has(tenderId);
      });
      
      // Process notifications with additional data and mark as read/unread
      const processedNotifications: TenderNotification[] = unseenTenders.map(tender => {
        const tenderData = tender.tender_data as TenderData;
        
        // Extract title from various possible sources
        const title = extractText(tenderData?.["notice-title"]) || 
                      extractText(tenderData?.title) || 
                      `${t('tenders.tender')} ${tenderData?.id || tender.id}`;
        
        // Extract organization from various possible sources
        const organization = extractText(tenderData?.["buyer-name"]) || 
                             extractText(tenderData?.organization_name) || 
                             extractText(tenderData?.organization) || 
                             t('notifications.unknownOrganization');
        
        // Prioritize using DT as the main deadline field, falling back to other date fields
        const deadlineDate = getMainDeadline(tenderData) || 
          extractText(tenderData?.["deadline-receipt-tender-date-lot"]) || 
          extractText(tenderData?.["deadline-date-lot"]) ||
          extractText(tenderData?.deadline);

        // Create a meaningful ID for the notification
        const tenderId = typeof tenderData === 'object' ?
                        (tenderData?.publication_number || 
                        tenderData?.id || 
                        tender.tender_id || 
                        tender.id) : tender.id;
        
        return {
          id: tender.id,
          tender_data: tenderData,
          created_at: tender.created_at,
          tender_id: tenderId,
          is_read: false, // These are all unseen tenders
          title: title,
          organization: organization,
          deadline: deadlineDate
        };
      });
      
      setNotifications(processedNotifications);
      setUnreadCount(processedNotifications.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.errorFetching'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [t, toast]);

  const markAsRead = async (notificationId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      // Add to user_seen_tenders to mark it as seen
      await supabase
        .from('user_seen_tenders')
        .upsert([
          {
            user_id: user.id,
            tender_id: notificationId,
          },
        ]);
      
      // Update local state to mark as read
      setNotifications(notifications.map(n => 
        n.tender_id === notificationId ? { ...n, is_read: true } : n
      ));
      
      // Update unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.errorMarkingAsRead'),
        variant: 'destructive',
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) return;
      
      // Get all unread notification IDs
      const unreadIds = notifications
        .filter(n => !n.is_read)
        .map(n => ({ user_id: user.id, tender_id: n.tender_id }));
      
      if (unreadIds.length === 0) return;
      
      // Mark all as read in database
      await supabase
        .from('user_seen_tenders')
        .upsert(unreadIds);
      
      // Update local state
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
      
      toast({
        title: t('notifications.allRead'),
        description: t('notifications.allReadDescription'),
      });
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
      toast({
        title: t('common.error'),
        description: t('notifications.errorMarkingAsRead'),
        variant: 'destructive',
      });
    }
  };

  // Initial fetch
  useEffect(() => {
    fetchNotifications();
    
    // Set up a periodic refresh (every 5 minutes)
    const intervalId = setInterval(fetchNotifications, 300000);
    return () => {
      clearInterval(intervalId);
    };
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    isLoading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
  };
};
